import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, query, orderByChild, equalTo } from "firebase/database";
import { getAuth } from 'firebase/auth';
import './EmailNodeSidebar.css';

const EmailNodeSidebar = ({ node, onClose, onUpdateNode, onEditEmail }) => {
    const [nodeData, setNodeData] = useState(node.data);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(true);

    const db = getDatabase();
    const auth = getAuth();
    const currentUser = auth.currentUser;

    // Load email templates
    useEffect(() => {
        if (currentUser) {
            const templatesQuery = query(
                ref(db, 'emailTemplates'),
                orderByChild('userId'),
                equalTo(currentUser.uid)
            );

            onValue(templatesQuery, (snapshot) => {
                if (snapshot.exists()) {
                    const templatesData = [];
                    snapshot.forEach((childSnapshot) => {
                        templatesData.push({
                            id: childSnapshot.key,
                            ...childSnapshot.val()
                        });
                    });
                    setEmailTemplates(templatesData);
                } else {
                    setEmailTemplates([]);
                }
                setLoading(false);
            });
        }
    }, [currentUser, db]);

    const handleDataChange = (field, value) => {
        const updatedData = { ...nodeData, [field]: value };
        setNodeData(updatedData);
        onUpdateNode(node.id, updatedData);
    };

    const handleTemplateSelect = (templateId, templateName) => {
        handleDataChange('templateId', templateId);
        handleDataChange('templateName', templateName);
    };

    const renderNodeConfig = () => {
        switch (node.type) {
            case 'email':
                return (
                    <div className="email-config-section">
                        <h5>Email Configuration</h5>
                        
                        <div className="email-config-group">
                            <label>Email Name</label>
                            <input
                                type="text"
                                value={nodeData.label || ''}
                                onChange={(e) => handleDataChange('label', e.target.value)}
                                placeholder="e.g. Welcome Email"
                            />
                        </div>

                        <div className="email-config-group">
                            <label>Subject Line</label>
                            <input
                                type="text"
                                value={nodeData.subject || ''}
                                onChange={(e) => handleDataChange('subject', e.target.value)}
                                placeholder="e.g. Welcome to our community!"
                            />
                        </div>

                        <div className="email-config-group">
                            <label>Email Template</label>
                            <select
                                value={nodeData.templateId || ''}
                                onChange={(e) => {
                                    const selectedTemplate = emailTemplates.find(t => t.id === e.target.value);
                                    handleTemplateSelect(e.target.value, selectedTemplate?.name);
                                }}
                            >
                                <option value="">Select a template...</option>
                                {emailTemplates.map(template => (
                                    <option key={template.id} value={template.id}>
                                        {template.name}
                                    </option>
                                ))}
                            </select>
                            
                            <div className="email-template-actions">
                                <button 
                                    className="email-template-btn primary"
                                    onClick={() => onEditEmail('new')}
                                >
                                    📧 Create New Template
                                </button>
                                {nodeData.templateId && (
                                    <button 
                                        className="email-template-btn secondary"
                                        onClick={() => onEditEmail(nodeData.templateId)}
                                    >
                                        ✏️ Edit Template
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="email-config-group">
                            <label>Send Delay</label>
                            <select
                                value={nodeData.sendDelay || 'immediate'}
                                onChange={(e) => handleDataChange('sendDelay', e.target.value)}
                            >
                                <option value="immediate">Send Immediately</option>
                                <option value="1hour">Wait 1 Hour</option>
                                <option value="24hours">Wait 24 Hours</option>
                                <option value="3days">Wait 3 Days</option>
                                <option value="1week">Wait 1 Week</option>
                            </select>
                        </div>
                    </div>
                );

            case 'delay':
                return (
                    <div className="email-config-section">
                        <h5>Delay Configuration</h5>
                        
                        <div className="email-config-group">
                            <label>Delay Name</label>
                            <input
                                type="text"
                                value={nodeData.label || ''}
                                onChange={(e) => handleDataChange('label', e.target.value)}
                                placeholder="e.g. Wait 24 hours"
                            />
                        </div>

                        <div className="email-config-row">
                            <div className="email-config-group">
                                <label>Duration</label>
                                <input
                                    type="number"
                                    value={nodeData.delayAmount || 1}
                                    onChange={(e) => handleDataChange('delayAmount', parseInt(e.target.value))}
                                    min="1"
                                />
                            </div>
                            <div className="email-config-group">
                                <label>Unit</label>
                                <select
                                    value={nodeData.delayType || 'hours'}
                                    onChange={(e) => handleDataChange('delayType', e.target.value)}
                                >
                                    <option value="minutes">Minutes</option>
                                    <option value="hours">Hours</option>
                                    <option value="days">Days</option>
                                    <option value="weeks">Weeks</option>
                                </select>
                            </div>
                        </div>
                    </div>
                );

            case 'condition':
                return (
                    <div className="email-config-section">
                        <h5>Condition Configuration</h5>
                        
                        <div className="email-config-group">
                            <label>Condition Name</label>
                            <input
                                type="text"
                                value={nodeData.label || ''}
                                onChange={(e) => handleDataChange('label', e.target.value)}
                                placeholder="e.g. Check Email Opened"
                            />
                        </div>

                        <div className="email-config-group">
                            <label>Condition Type</label>
                            <select
                                value={nodeData.conditionType || 'email_opened'}
                                onChange={(e) => handleDataChange('conditionType', e.target.value)}
                            >
                                <option value="email_opened">Email Opened</option>
                                <option value="email_clicked">Email Clicked</option>
                                <option value="time_passed">Time Passed</option>
                                <option value="tag_added">Tag Added</option>
                            </select>
                        </div>
                    </div>
                );

            case 'trigger':
                return (
                    <div className="email-config-section">
                        <h5>Trigger Configuration</h5>
                        
                        <div className="email-config-group">
                            <label>Trigger Name</label>
                            <input
                                type="text"
                                value={nodeData.label || ''}
                                onChange={(e) => handleDataChange('label', e.target.value)}
                                placeholder="e.g. Flow Start"
                            />
                        </div>

                        <div className="email-config-group">
                            <label>Trigger Type</label>
                            <select
                                value={nodeData.triggerType || 'manual'}
                                onChange={(e) => handleDataChange('triggerType', e.target.value)}
                            >
                                <option value="manual">Manual Start</option>
                                <option value="product_purchase">Product Purchase</option>
                                <option value="form_submit">Form Submit</option>
                                <option value="tag_added">Tag Added</option>
                                <option value="date_trigger">Date/Time</option>
                            </select>
                        </div>
                    </div>
                );

            default:
                return <div>Select a node to configure</div>;
        }
    };

    return (
        <div className="email-node-sidebar">
            <div className="email-sidebar-header">
                <h4>Node Configuration</h4>
                <button className="email-sidebar-close" onClick={onClose}>
                    ✕
                </button>
            </div>
            
            <div className="email-sidebar-content">
                {renderNodeConfig()}
            </div>
        </div>
    );
};

export default EmailNodeSidebar;
